import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Container,
  Form,
  FormField,
  Group,
  Illustration,
  Link,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  Text,
  TextField,
  useMelioForm,
} from '@melio/penny';
import { object, string } from 'yup';

import { forgotPassword } from '../../api/auth';
import { useAnalytics, type Cta } from '../../analytics';
import { prefetchIllustration } from '../../theme/melioTheme';
import { getEntryPoint } from '../../session';
import { peekStoredAuthorizeUrl } from '../../hooks/useRedirectParam';

interface ForgotPasswordFormModel {
  email: string;
}

const useForgotPasswordValidationSchema = () => {
  return object().shape({
    email: string().email('Please enter a valid email address').required('Enter a valid email address.'),
  });
};

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const schema = useForgotPasswordValidationSchema();

  const [submittedEmail, setSubmittedEmail] = useState<string>('');

  const trackView = (pageName: 'forgot-password' | 'check-email') => {
    track({
      eventName: 'Connection-View',
      properties: {
        PageName: pageName,
        Flow: 'sign-in',
        Intent: 'send-reset-link',
        EntryPoint: getEntryPoint(),
      },
    });
  };

  const trackClick = (cta: Cta) => {
    track({
      eventName: 'Connection-Click',
      properties: {
        PageName: 'forgot-password',
        Flow: 'sign-in',
        Intent: 'send-reset-link',
        EntryPoint: getEntryPoint(),
        Cta: cta,
        EmailInteraction: !!formState.touchedFields.email,
      },
    });
  };

  const trackStatus = (statusType: 'success' | 'failure', cta: Cta, errorType?: 'invalid-email') => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: 'forgot-password',
        Flow: 'sign-in',
        Intent: 'send-reset-link',
        EntryPoint: getEntryPoint(),
        Cta: cta,
        StatusType: statusType,
        ...(errorType && { ErrorType: errorType }),
      },
    });
  };

  // Track page view on mount and prefetch the success illustration
  useEffect(() => {
    if (!isSubmitted) {
      trackView('forgot-password');
      prefetchIllustration('new-email');
    }
  }, []);

  // Track check-email view when submitted
  useEffect(() => {
    if (isSubmitted) {
      trackView('check-email');
    }
  }, [isSubmitted]);

  const { formProps, registerField, handleSubmit, formState } = useMelioForm<ForgotPasswordFormModel>({
    onSubmit: async (data) => {
      setIsLoading(true);
      setError(null);
      trackClick('send-reset-link');

      const result = await forgotPassword({ email: data.email, redirect: peekStoredAuthorizeUrl() ?? undefined });

      if (result.type === 'error') {
        trackStatus('failure', 'send-reset-link', 'invalid-email');
        setError(result.message || 'Something went wrong. Please try again.');
        setIsLoading(false);
        return;
      }

      trackStatus('success', 'send-reset-link');
      setSubmittedEmail(data.email);
      setIsSubmitted(true);
      setIsLoading(false);
    },
    schema,
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = handleSubmit();

  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleGoBack = (e: React.MouseEvent) => {
    e.preventDefault();
    trackClick('go-back');
    navigate({ pathname: '/sign-in', search: location.search });
  };

  if (isSubmitted) {
    return (
      <Container height="full" paddingX="s" paddingTop="xxl" data-testid="forgot-password-page">
        <Container overflow="visible" style={{ width: '100%', maxWidth: '480px' }}>
          <Group variant="vertical" spacing="l" alignItems="stretch">
            {/* Banner at top */}
            <SectionBannerRoot variant="success" data-testid="email-sent-banner">
              <SectionBannerIcon />
              <SectionBannerContent>
                <SectionBannerDescription>We&apos;ve emailed you a link to reset your password.</SectionBannerDescription>
              </SectionBannerContent>
            </SectionBannerRoot>

            {/* Main content */}
            <Group variant="vertical" spacing="m" alignItems="center">
              {/* Illustration */}
              <Illustration size="small" type="new-email" />

              <Group variant="vertical" spacing="xs" alignItems="center">
                <Text textStyle="heading1Semi" color="semantic.text.primary" data-testid="forgot-password-success-title">
                  Check your email
                </Text>
                <Text textStyle="body2" color="semantic.text.primary" style={{ textAlign: 'center' }}>
                  We&apos;ve sent an email to{' '}
                  <Text as="span" textStyle="body2Semi" color="semantic.text.primary">
                    {submittedEmail}
                  </Text>{' '}
                  with instructions to reset your password.
                </Text>
              </Group>
            </Group>
          </Group>
        </Container>
      </Container>
    );
  }

  return (
    <Container height="full" paddingX="s" paddingTop="xxl" data-testid="forgot-password-page">
      <Container overflow="visible" style={{ width: '100%', maxWidth: '480px' }}>
        <Group variant="vertical" spacing="l" alignItems="stretch">
          {/* Header */}
          <Group variant="vertical" spacing="xs">
            <Text textStyle="heading1Semi" color="semantic.text.primary" data-testid="forgot-password-page-title">
              Reset password
            </Text>
            <Text textStyle="body2" color="semantic.text.secondary">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </Text>
          </Group>

          {/* Email Form */}
          <Form {...formProps} onKeyDown={handleFormKeyDown} columns={1} autoComplete="on" data-testid="forgot-password-form">
            <Group variant="vertical" spacing="xs">
              <FormField
                {...registerField('email')}
                labelProps={{ label: 'Email address' }}
                isRequired
                render={(fieldProps) => (
                  <TextField
                    {...fieldProps}
                    type="email"
                    placeholder="Enter your email"
                    isInvalid={!!error}
                    autoComplete="email"
                    data-testid="email-input"
                  />
                )}
              />
              {error && (
                <Text textStyle="body4" color="semantic.text.critical.rest" data-testid="forgot-password-error-message">
                  {error}
                </Text>
              )}
            </Group>

            <Group variant="vertical" spacing="s" alignItems="stretch">
              <FormField
                render={() => (
                  <Button
                    label="Send reset link"
                    variant="primary"
                    size="large"
                    isFullWidth
                    isLoading={isLoading}
                    onClick={() => onSubmit()}
                    data-testid="send-reset-link-button"
                  />
                )}
              />
              <Group variant="horizontal" justifyContent="center">
                <Link
                  href="#"
                  variant="standalone"
                  size="medium"
                  color="secondary"
                  label="Go back"
                  onClick={handleGoBack}
                  data-testid="go-back-link"
                />
              </Group>
            </Group>
          </Form>
        </Group>
      </Container>
    </Container>
  );
};
